import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings as SettingsIcon, Save, AlertTriangle, Plus, Trash2, GripVertical, ShoppingCart, Store, CheckCircle, XCircle, Loader2, Search } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import AiSettingsSection from '@/components/admin/AiSettingsSection';
import AiCreditsSection from '@/components/admin/AiCreditsSection';
import StorageSection from '@/components/admin/StorageSection';
import BrandLogoSection from '@/components/admin/BrandLogoSection';
import AiObservabilitySection from '@/components/admin/AiObservabilitySection';
import ImageUploader from '@/components/admin/ImageUploader';
import { IS_LODGING, SITE_NAME } from '@/config/site';
import { Bot } from 'lucide-react';
import type { DippyConfig } from '@/hooks/useDippySettings';
import { DIPPY_DEFAULTS } from '@/hooks/useDippySettings';
import AssetField from '@/components/admin/AssetField';

interface MaintenanceModeData {
  enabled: boolean;
  title: string;
  message: string;
  estimated_end: string;
}

interface NavLink {
  label: string;
  href?: string;
  axissOpen?: boolean;
  hide_when_authed?: boolean;
}

interface SettingsData {
  store_name: string;
  tagline: string;
  contact_email: string;
  contact_phone: string;
  store_logo_url: string;
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  pinterest_url: string;
  youtube_url: string;
  default_meta_title: string;
  default_meta_description: string;
  free_shipping_threshold: string;
  tax_rate: string;
  tax_included_in_price: boolean;
  maintenance_mode: MaintenanceModeData;
  navigation_links: NavLink[];
  footer_shop_links: NavLink[];
  footer_support_links: NavLink[];
  footer_newsletter_title: string;
  footer_newsletter_subtitle: string;
  blog_page_title: string;
  blog_page_description: string;
  default_og_image: string;
  dippy_config: DippyConfig;
  shop_quick_add_enabled: boolean;
  // Header chrome — top-bar action icons. Independent of nav links.
  header_search_enabled: boolean;
  // Authed-user surface flags. Used together when a tenant's authed UX is
  // an installed custom app and marketing scaffolding should fall away.
  redirect_authed_to_app: boolean;
  hide_nav_when_authed: boolean;
  hide_value_props_when_authed: boolean;
  // Lodging host identity. Empty means "fall back to the site name / default
  // copy", so these are only sent for lodging tenants.
  host_name: string;
  host_photo_url: string;
  host_bio: string;
}

function LinkListEditor({ links, onChange, label, showHideWhenAuthed }: { links: NavLink[]; onChange: (links: NavLink[]) => void; label: string; showHideWhenAuthed?: boolean }) {
  function updateLink(index: number, field: string, value: string | boolean) {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  }

  function addLink() {
    onChange([...links, { label: '', href: '' }]);
  }

  function removeLink(index: number) {
    onChange(links.filter((_, i) => i !== index));
  }

  function moveLink(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= links.length) return;
    const updated = [...links];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    onChange(updated);
  }

  return (
    <div className="space-y-3">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      {links.map((link, i) => (
        <div key={i} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex flex-col gap-0.5">
            <button type="button" onClick={() => moveLink(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-0.5" aria-label="Move up">
              <GripVertical className="h-3 w-3" />
            </button>
          </div>
          <Input
            value={link.label}
            onChange={(e) => updateLink(i, 'label', e.target.value)}
            placeholder="Label"
            className="flex-1"
          />
          {link.axissOpen ? (
            <span className="text-xs text-gray-500 px-2 whitespace-nowrap">Opens chat</span>
          ) : (
            <Input
              value={link.href || ''}
              onChange={(e) => updateLink(i, 'href', e.target.value)}
              placeholder="/path"
              className="flex-1"
            />
          )}
          <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
            <input
              type="checkbox"
              checked={link.axissOpen || false}
              onChange={(e) => {
                const updated = [...links];
                if (e.target.checked) {
                  updated[i] = { label: link.label, axissOpen: true };
                } else {
                  updated[i] = { label: link.label, href: '' };
                }
                onChange(updated);
              }}
              className="w-3.5 h-3.5"
            />
            Chat
          </label>
          {showHideWhenAuthed && (
            <label className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap" title="Hide this link from users who are signed in. Useful for marketing-only links like Pricing.">
              <input
                type="checkbox"
                checked={link.hide_when_authed || false}
                onChange={(e) => updateLink(i, 'hide_when_authed', e.target.checked)}
                className="w-3.5 h-3.5"
              />
              Hide when signed in
            </label>
          )}
          <button type="button" onClick={() => removeLink(i)} className="text-red-400 hover:text-red-600 p-1" aria-label={`Remove ${link.label}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={addLink}>
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Link
      </Button>
    </div>
  );
}

interface SettingsResponse {
  settings: Record<string, any>;
}

export default function Settings() {
  const [settings, setSettings] = useState<SettingsData>({
    store_name: '',
    tagline: '',
    contact_email: '',
    contact_phone: '',
    store_logo_url: '',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    pinterest_url: '',
    youtube_url: '',
    default_meta_title: '',
    default_meta_description: '',
    free_shipping_threshold: '',
    tax_rate: '',
    tax_included_in_price: false,
    maintenance_mode: {
      enabled: false,
      title: "We'll Be Right Back",
      message: "We're currently performing scheduled maintenance. Please check back shortly.",
      estimated_end: '',
    },
    navigation_links: [],
    footer_shop_links: [],
    footer_support_links: [],
    footer_newsletter_title: '',
    footer_newsletter_subtitle: '',
    blog_page_title: '',
    blog_page_description: '',
    default_og_image: '',
    dippy_config: { ...DIPPY_DEFAULTS },
    shop_quick_add_enabled: true,
    header_search_enabled: true,
    redirect_authed_to_app: false,
    hide_nav_when_authed: false,
    hide_value_props_when_authed: false,
    host_name: '',
    host_photo_url: '',
    host_bio: '',
  });

  // Snapshot of the last-saved (or last-loaded) settings. Used to detect
  // unsaved changes so the floating save bar only appears when something
  // actually differs from what's persisted.
  const [savedSettings, setSavedSettings] = useState<SettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isDirty = savedSettings !== null && JSON.stringify(settings) !== JSON.stringify(savedSettings);

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<SettingsResponse>('/admin/settings');

      const loaded: SettingsData = {
        store_name: response.settings.store_name || '',
        tagline: response.settings.tagline || '',
        contact_email: response.settings.contact_email || '',
        contact_phone: response.settings.contact_phone || '',
        store_logo_url: response.settings.store_logo_url || '',
        facebook_url: response.settings.facebook_url || '',
        instagram_url: response.settings.instagram_url || '',
        twitter_url: response.settings.twitter_url || '',
        pinterest_url: response.settings.pinterest_url || '',
        youtube_url: response.settings.youtube_url || '',
        default_meta_title: response.settings.default_meta_title || '',
        default_meta_description: response.settings.default_meta_description || '',
        free_shipping_threshold: response.settings.free_shipping_threshold?.toString() || '',
        tax_rate: response.settings.tax_rate?.toString() || '',
        tax_included_in_price: response.settings.tax_included_in_price || false,
        maintenance_mode: {
          enabled: response.settings.maintenance_mode?.enabled || false,
          title: response.settings.maintenance_mode?.title || "We'll Be Right Back",
          message: response.settings.maintenance_mode?.message || "We're currently performing scheduled maintenance. Please check back shortly.",
          estimated_end: response.settings.maintenance_mode?.estimated_end || '',
        },
        navigation_links: response.settings.navigation_links || [],
        footer_shop_links: response.settings.footer_shop_links || [],
        footer_support_links: response.settings.footer_support_links || [],
        footer_newsletter_title: response.settings.footer_newsletter_title || '',
        footer_newsletter_subtitle: response.settings.footer_newsletter_subtitle || '',
        blog_page_title: response.settings.blog_page_title || '',
        blog_page_description: response.settings.blog_page_description || '',
        default_og_image: response.settings.default_og_image || '',
        dippy_config: { ...DIPPY_DEFAULTS, ...(response.settings.dippy_config || {}) },
        shop_quick_add_enabled: response.settings.shop_quick_add_enabled !== false,
        header_search_enabled: response.settings.header_search_enabled !== false,
        redirect_authed_to_app: response.settings.redirect_authed_to_app === true,
        hide_nav_when_authed: response.settings.hide_nav_when_authed === true,
        hide_value_props_when_authed: response.settings.hide_value_props_when_authed === true,
        host_name: response.settings.host_name || '',
        host_photo_url: response.settings.host_photo_url || '',
        host_bio: response.settings.host_bio || '',
      };

      setSettings(loaded);
      setSavedSettings(loaded);
    } catch (err) {
      setError('Failed to load settings. Please try again.');
      console.error('Error fetching settings:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);
    setSaveError(null);

    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        settings: {
          store_name: settings.store_name,
          tagline: settings.tagline,
          contact_email: settings.contact_email,
          contact_phone: settings.contact_phone,
          store_logo_url: settings.store_logo_url,
          facebook_url: settings.facebook_url,
          instagram_url: settings.instagram_url,
          twitter_url: settings.twitter_url,
          pinterest_url: settings.pinterest_url,
          youtube_url: settings.youtube_url,
          default_meta_title: settings.default_meta_title,
          default_meta_description: settings.default_meta_description,
          free_shipping_threshold: settings.free_shipping_threshold ? parseFloat(settings.free_shipping_threshold) : null,
          tax_rate: settings.tax_rate ? parseFloat(settings.tax_rate) : 0,
          tax_included_in_price: settings.tax_included_in_price,
          maintenance_mode: {
            enabled: settings.maintenance_mode.enabled,
            title: settings.maintenance_mode.title,
            message: settings.maintenance_mode.message,
            estimated_end: settings.maintenance_mode.estimated_end || null,
          },
          navigation_links: settings.navigation_links,
          footer_shop_links: settings.footer_shop_links,
          footer_support_links: settings.footer_support_links,
          footer_newsletter_title: settings.footer_newsletter_title,
          footer_newsletter_subtitle: settings.footer_newsletter_subtitle,
          blog_page_title: settings.blog_page_title,
          blog_page_description: settings.blog_page_description,
          default_og_image: settings.default_og_image || null,
          dippy_config: settings.dippy_config,
          shop_quick_add_enabled: settings.shop_quick_add_enabled,
          header_search_enabled: settings.header_search_enabled,
          redirect_authed_to_app: settings.redirect_authed_to_app,
          hide_nav_when_authed: settings.hide_nav_when_authed,
          hide_value_props_when_authed: settings.hide_value_props_when_authed,
          // Only lodging tenants surface the Host card, so only they persist
          // these keys — every other tenant would write three empty rows.
          ...(IS_LODGING
            ? {
                host_name: settings.host_name || null,
                host_photo_url: settings.host_photo_url || null,
                host_bio: settings.host_bio || null,
              }
            : {}),
        },
      };

      await api.put('/admin/settings', payload);
      setSavedSettings(settings);
      setSuccessMessage('Settings saved successfully');

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // Surface the backend's actual reason (e.g. "contact_email must be a
      // valid email") instead of a generic message — api.ts puts it on .message.
      setSaveError(err instanceof Error && err.message ? err.message : 'Failed to save settings. Please try again.');
      console.error('Error saving settings:', err);
    } finally {
      setIsSaving(false);
    }
  }

  function handleDiscard() {
    if (savedSettings) setSettings(savedSettings);
    setSaveError(null);
    setSuccessMessage(null);
  }

  function handleChange(field: keyof SettingsData, value: string | boolean) {
    setSettings(prev => ({ ...prev, [field]: value }));
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading settings...</span>
      </div>
    );
  }

  const showSaveBar = isDirty || isSaving || !!successMessage || !!saveError;

  return (
    <div className={cn('container mx-auto px-4 py-8 max-w-4xl', showSaveBar && 'pb-28')}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <SettingsIcon className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        </div>
        <p className="text-gray-600">Manage your store settings and configuration</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form id="settings-form" onSubmit={handleSave} noValidate>
        <div className="space-y-6">
          {/* Maintenance Mode */}
          <Card
            padding="lg"
            className={settings.maintenance_mode.enabled ? 'border-amber-400 bg-amber-50/50' : ''}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-5 h-5 ${settings.maintenance_mode.enabled ? 'text-amber-600' : 'text-stone-400'}`} aria-hidden="true" />
                <h3 className="text-lg font-semibold text-stone-900">Maintenance Mode</h3>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.maintenance_mode.enabled}
                onClick={() => {
                  if (!settings.maintenance_mode.enabled) {
                    if (!confirm('Enable maintenance mode? The public storefront will be taken offline. Admins can still access the site.')) return;
                  }
                  setSettings(prev => ({
                    ...prev,
                    maintenance_mode: { ...prev.maintenance_mode, enabled: !prev.maintenance_mode.enabled },
                  }));
                }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  settings.maintenance_mode.enabled ? 'bg-amber-500' : 'bg-stone-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform ${
                    settings.maintenance_mode.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {settings.maintenance_mode.enabled && (
              <div className="space-y-4 pt-2 border-t border-amber-200">
                <p className="text-sm text-amber-700 mt-3">
                  The public storefront is offline. Remember to save settings to apply changes.
                </p>
                <Input
                  label="Title"
                  value={settings.maintenance_mode.title}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    maintenance_mode: { ...prev.maintenance_mode, title: e.target.value },
                  }))}
                  placeholder="We'll Be Right Back"
                />
                <Textarea
                  label="Message"
                  value={settings.maintenance_mode.message}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    maintenance_mode: { ...prev.maintenance_mode, message: e.target.value },
                  }))}
                  placeholder="We're currently performing scheduled maintenance..."
                  rows={3}
                />
                <Input
                  label="Estimated End (optional)"
                  type="datetime-local"
                  value={settings.maintenance_mode.estimated_end}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    maintenance_mode: { ...prev.maintenance_mode, estimated_end: e.target.value },
                  }))}
                  helperText="When do you expect the site to be back? Leave empty if unknown."
                />
              </div>
            )}
          </Card>

          <Card title="Store Information" padding="lg">
            <div className="space-y-6">
              <Input
                label="Store Name"
                value={settings.store_name}
                onChange={(e) => handleChange('store_name', e.target.value)}
                placeholder="Your Store Name"
                required
                aria-required="true"
              />

              <Textarea
                label="Tagline"
                value={settings.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Your store tagline"
                rows={2}
                helperText="A short description of your store"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Contact Email"
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  placeholder="support@yoursite.com"
                  required
                  aria-required="true"
                />

                <Input
                  label="Contact Phone"
                  type="tel"
                  value={settings.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </Card>

          <Card padding="lg">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-5 h-5 text-sage-600" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-stone-900">Shop Display</h3>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="shop_quick_add_enabled"
                checked={settings.shop_quick_add_enabled}
                onChange={(e) => handleChange('shop_quick_add_enabled', e.target.checked)}
                className="w-4 h-4 text-sage-600 border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
              />
              <label htmlFor="shop_quick_add_enabled" className="text-sm font-medium text-gray-700">
                Show "Quick Add to Cart" button on product cards
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500 ml-7">
              When enabled, customers can add products directly from the shop page without visiting the product detail page.
            </p>
          </Card>

          <Card padding="lg">
            <div className="flex items-center gap-2 mb-4">
              <Store className="w-5 h-5 text-sage-600" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-stone-900">When users are signed in</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Three layered controls for SaaS tenants whose authed users live
              inside an installed custom-app surface and don't need the
              marketing scaffolding.
            </p>

            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="redirect_authed_to_app"
                    checked={settings.redirect_authed_to_app}
                    onChange={(e) => handleChange('redirect_authed_to_app', e.target.checked)}
                    className="w-4 h-4 text-sage-600 border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
                  />
                  <label htmlFor="redirect_authed_to_app" className="text-sm font-medium text-gray-700">
                    Redirect from the marketing homepage to the installed app
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  When a signed-in user visits <code>/</code>, send them straight
                  to the first installed app instead of the marketing homepage.
                  Admins can bypass via <code>?marketing=1</code>.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hide_nav_when_authed"
                    checked={settings.hide_nav_when_authed}
                    onChange={(e) => handleChange('hide_nav_when_authed', e.target.checked)}
                    className="w-4 h-4 text-sage-600 border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
                  />
                  <label htmlFor="hide_nav_when_authed" className="text-sm font-medium text-gray-700">
                    Hide marketing navigation in the header
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  Removes every nav link from the top header for signed-in
                  users. App-shell links from installed apps still appear. For
                  per-link control instead, use the "Hide when authed" toggle
                  on individual links in the navigation editor below.
                </p>
              </div>

              <div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="hide_value_props_when_authed"
                    checked={settings.hide_value_props_when_authed}
                    onChange={(e) => handleChange('hide_value_props_when_authed', e.target.checked)}
                    className="w-4 h-4 text-sage-600 border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
                  />
                  <label htmlFor="hide_value_props_when_authed" className="text-sm font-medium text-gray-700">
                    Hide the value-propositions bar above the footer
                  </label>
                </div>
                <p className="mt-1 text-xs text-gray-500 ml-7">
                  Skips the four-icon value-props row that normally sits above
                  the footer on every non-homepage page.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Brand" padding="lg">
            <BrandLogoSection
              value={settings.store_logo_url}
              onChange={(url) => handleChange('store_logo_url', url)}
              storeName={settings.store_name}
              tagline={settings.tagline}
            />
          </Card>

          {IS_LODGING && (
            <Card title="Host" padding="lg">
              <p className="text-sm text-gray-600 mb-4">
                Shown in the &ldquo;Hosted by&rdquo; block on every property page, and used as the
                photo for the &ldquo;Meet your host&rdquo; homepage section when that section has no
                image of its own.
              </p>
              <div className="space-y-4">
                <ImageUploader
                  value={settings.host_photo_url}
                  onChange={(url) => handleChange('host_photo_url', url)}
                  label="Host photo"
                  targetWidth={512}
                  targetHeight={512}
                />
                <Input
                  label="Host name"
                  value={settings.host_name}
                  onChange={(e) => handleChange('host_name', e.target.value)}
                  placeholder={SITE_NAME}
                  helperText={`Leave blank to use your site name (${SITE_NAME}).`}
                />
                <Textarea
                  label="About the host"
                  value={settings.host_bio}
                  onChange={(e) => handleChange('host_bio', e.target.value)}
                  rows={4}
                  helperText="Leave blank to use the default direct-booking copy."
                />
              </div>
            </Card>
          )}

          <Card padding="lg">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-sage-600" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-stone-900">Header</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Toggles for action icons in the top header. Independent of
              navigation links below.
            </p>

            <div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="header_search_enabled"
                  checked={settings.header_search_enabled}
                  onChange={(e) => handleChange('header_search_enabled', e.target.checked)}
                  className="w-4 h-4 text-sage-600 border-gray-300 rounded focus:ring-2 focus:ring-sage-500"
                />
                <label htmlFor="header_search_enabled" className="text-sm font-medium text-gray-700">
                  Show the search icon in the header
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500 ml-7">
                When off, the magnifying-glass icon next to the cart is
                hidden. The <code>/search</code> page itself stays reachable
                at the direct URL — turn this off when your site doesn't
                have searchable content (e.g. a single-page brochure site).
              </p>
            </div>
          </Card>

          <Card title="Navigation Links" padding="lg">
            <div className="space-y-6">
              <p className="text-sm text-gray-500">Configure the links shown in the header navigation bar and mobile menu.</p>
              <LinkListEditor
                links={settings.navigation_links}
                onChange={(links) => setSettings(prev => ({ ...prev, navigation_links: links }))}
                label="Header Navigation"
                showHideWhenAuthed
              />
            </div>
          </Card>

          <Card title="Footer Content" padding="lg">
            <div className="space-y-6">
              <LinkListEditor
                links={settings.footer_shop_links}
                onChange={(links) => setSettings(prev => ({ ...prev, footer_shop_links: links }))}
                label="Shop Links"
              />
              <LinkListEditor
                links={settings.footer_support_links}
                onChange={(links) => setSettings(prev => ({ ...prev, footer_support_links: links }))}
                label="Support Links"
              />
              <Input
                label="Footer Newsletter Title"
                value={settings.footer_newsletter_title}
                onChange={(e) => handleChange('footer_newsletter_title', e.target.value)}
                placeholder="Stay Connected"
              />
              <Input
                label="Footer Newsletter Subtitle"
                value={settings.footer_newsletter_subtitle}
                onChange={(e) => handleChange('footer_newsletter_subtitle', e.target.value)}
                placeholder="Get growing tips, seasonal guides, and exclusive deals."
              />
            </div>
          </Card>

          <Card title="Blog Page" padding="lg">
            <div className="space-y-6">
              <Input
                label="Blog Page Title"
                value={settings.blog_page_title}
                onChange={(e) => handleChange('blog_page_title', e.target.value)}
                placeholder="Blog"
                helperText="Title shown on the blog listing page"
              />
              <Textarea
                label="Blog Page Description"
                value={settings.blog_page_description}
                onChange={(e) => handleChange('blog_page_description', e.target.value)}
                placeholder="News, tips, and stories"
                rows={2}
                helperText="Description shown below the blog title and used for SEO"
              />
            </div>
          </Card>

          <Card title="Social Media Links" padding="lg">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Facebook URL"
                  type="url"
                  value={settings.facebook_url}
                  onChange={(e) => handleChange('facebook_url', e.target.value)}
                  placeholder="https://facebook.com/yourstore"
                />

                <Input
                  label="Instagram URL"
                  type="url"
                  value={settings.instagram_url}
                  onChange={(e) => handleChange('instagram_url', e.target.value)}
                  placeholder="https://instagram.com/yourstore"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Twitter URL"
                  type="url"
                  value={settings.twitter_url}
                  onChange={(e) => handleChange('twitter_url', e.target.value)}
                  placeholder="https://twitter.com/yourstore"
                />

                <Input
                  label="Pinterest URL"
                  type="url"
                  value={settings.pinterest_url}
                  onChange={(e) => handleChange('pinterest_url', e.target.value)}
                  placeholder="https://pinterest.com/yourstore"
                />
              </div>

              <Input
                label="YouTube URL"
                type="url"
                value={settings.youtube_url}
                onChange={(e) => handleChange('youtube_url', e.target.value)}
                placeholder="https://youtube.com/@yourstore"
              />
            </div>
          </Card>

          <Card title="Search Engine Optimization" padding="lg">
            <div className="space-y-6">
              <Input
                label="Default Meta Title"
                value={settings.default_meta_title}
                onChange={(e) => handleChange('default_meta_title', e.target.value)}
                placeholder="Your Store Name"
                helperText="Default title for search engines (50-60 characters recommended)"
              />

              <Textarea
                label="Default Meta Description"
                value={settings.default_meta_description}
                onChange={(e) => handleChange('default_meta_description', e.target.value)}
                placeholder="Discover our products and services"
                rows={3}
                helperText="Default description for search engines (150-160 characters recommended)"
              />

              <AssetField
                specKey="default_og_image"
                label="Default social sharing image"
                value={settings.default_og_image}
                onChange={(url) => handleChange('default_og_image', url)}
                helperText="What Facebook, LinkedIn and iMessage show when someone shares a page that has no image of its own."
              />
            </div>
          </Card>

          <Card title="Shipping" padding="lg">
            <Input
              label="Free Shipping Threshold"
              type="number"
              value={settings.free_shipping_threshold}
              onChange={(e) => handleChange('free_shipping_threshold', e.target.value)}
              placeholder="50.00"
              step="0.01"
              helperText="Minimum order amount for free shipping (leave empty to disable)"
            />
          </Card>

          <Card title="Tax Settings" padding="lg">
            <div className="space-y-6">
              <Input
                label="Tax Rate"
                type="number"
                value={settings.tax_rate}
                onChange={(e) => handleChange('tax_rate', e.target.value)}
                placeholder="8.5"
                step="0.01"
                helperText="Tax rate as a percentage (e.g., 8.5 for 8.5%)"
              />

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="tax_included_in_price"
                  checked={settings.tax_included_in_price}
                  onChange={(e) => handleChange('tax_included_in_price', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="tax_included_in_price" className="text-sm font-medium text-gray-700">
                  Tax is included in product prices
                </label>
              </div>
            </div>
          </Card>
        </div>

          {/* AI Assistant (Dippy) */}
          <Card padding="lg">
            <div className="flex items-center gap-2 mb-4">
              <Bot className="w-5 h-5 text-green-600" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-stone-900">AI Assistant ({settings.dippy_config.name})</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 w-32">Enabled</label>
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.dippy_config.enabled}
                  onClick={() => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, enabled: !prev.dippy_config.enabled },
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.dippy_config.enabled ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    settings.dippy_config.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center gap-3">
                <label htmlFor="dippy_name" className="text-sm font-medium text-gray-700 w-32">Name</label>
                <Input
                  id="dippy_name"
                  value={settings.dippy_config.name}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, name: e.target.value },
                  }))}
                  className="max-w-xs"
                />
              </div>

              <div className="flex items-center gap-3">
                <label htmlFor="dippy_avatar" className="text-sm font-medium text-gray-700 w-32">Character</label>
                <select
                  id="dippy_avatar"
                  value={settings.dippy_config.avatar}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, avatar: e.target.value as 'seedling' | 'robot' },
                  }))}
                  className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="seedling">🌱 Seedling</option>
                  <option value="robot">🤖 Robot</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label htmlFor="dippy_alert_style" className="text-sm font-medium text-gray-700 w-32">Alert Style</label>
                <select
                  id="dippy_alert_style"
                  value={settings.dippy_config.alert_style}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, alert_style: e.target.value as 'speech_bubble' | 'bounce_badge' },
                  }))}
                  className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="speech_bubble">Speech Bubble</option>
                  <option value="bounce_badge">Bounce + Badge</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 w-32">Show Tips</span>
                <input
                  type="checkbox"
                  checked={settings.dippy_config.show_tips}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, show_tips: e.target.checked },
                  }))}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 w-32">Show Alerts</span>
                <input
                  type="checkbox"
                  checked={settings.dippy_config.show_alerts}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    dippy_config: { ...prev.dippy_config, show_alerts: e.target.checked },
                  }))}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex items-center gap-3">
                <label htmlFor="dippy_poll" className="text-sm font-medium text-gray-700 w-32">Poll Interval</label>
                <div className="flex items-center gap-2">
                  <input
                    id="dippy_poll"
                    type="number"
                    min="10"
                    max="300"
                    value={settings.dippy_config.poll_interval_seconds}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      dippy_config: { ...prev.dippy_config, poll_interval_seconds: parseInt(e.target.value) || 30 },
                    }))}
                    className="w-20 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-500">seconds</span>
                </div>
              </div>
            </div>
          </Card>

      </form>

      <div className="mt-8">
        <StorageSection />
      </div>

      <div className="mt-8">
        <AiCreditsSection />
      </div>

      <div className="mt-8">
        <AiSettingsSection />
      </div>

      <div className="mt-8">
        <AiObservabilitySection />
      </div>

      <MarketplaceCredentials />

      {/* Floating save bar — appears as soon as a change is made, so the
          Save button is always reachable without scrolling to the bottom. */}
      {showSaveBar && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur">
          <div className="container mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1 text-sm" role="status" aria-live="polite">
              {saveError ? (
                <span className="text-red-700">{saveError}</span>
              ) : successMessage ? (
                <span className="text-green-700">{successMessage}</span>
              ) : (
                <span className="text-gray-500">You have unsaved changes</span>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={handleDiscard}
              disabled={isSaving || !isDirty}
            >
              Discard
            </Button>
            <Button
              type="submit"
              form="settings-form"
              variant="primary"
              size="md"
              isLoading={isSaving}
              disabled={!isDirty}
            >
              <Save className="w-5 h-5 mr-2" aria-hidden="true" />
              Save Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarketplaceCredentials() {
  const [credentials, setCredentials] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/admin/agent/marketplace-credentials')
      .then((res: any) => { setCredentials(res?.alibaba ?? res); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    setMessage('');
    try {
      const res: any = await api.put('/admin/agent/marketplace-credentials', {
        alibaba_app_key: appKey || undefined,
        alibaba_app_secret: appSecret || undefined,
      });
      setCredentials(res?.alibaba ?? res);
      setAppKey('');
      setAppSecret('');
      setMessage('Credentials saved successfully');
    } catch {
      setMessage('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="py-4"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;

  const hasSite = credentials?.has_site_credentials;
  const hasPlatform = credentials?.has_platform_credentials;
  const hasEnv = credentials?.has_env_credentials;
  const source = credentials?.source;

  let badge: { label: string; cls: string; Icon: typeof CheckCircle } = {
    label: 'Not configured',
    cls: 'bg-stone-100 text-stone-600',
    Icon: XCircle,
  };
  if (hasSite) badge = { label: 'Site override active', cls: 'bg-green-100 text-green-800', Icon: CheckCircle };
  else if (hasPlatform) badge = { label: 'Using platform default', cls: 'bg-blue-100 text-blue-800', Icon: CheckCircle };
  else if (hasEnv) badge = { label: 'Using environment', cls: 'bg-blue-100 text-blue-800', Icon: CheckCircle };

  return (
    <div className="mt-8 border-t border-stone-200 pt-8">
      <div className="flex items-center gap-3 mb-3">
        <Store className="w-5 h-5 text-sage-700" />
        <h3 className="text-lg font-semibold text-stone-900">Marketplace Integrations</h3>
      </div>
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-stone-900">Alibaba Open Platform API</h4>
            <p className="text-sm text-stone-500">Used by Dippy to contact Alibaba suppliers directly</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
              <badge.Icon className="w-3.5 h-3.5" />
              {badge.label}
            </span>
          </div>
        </div>

        {credentials?.app_key_masked && (
          <p className="text-sm text-stone-500 mb-3">
            Active key ({source === 'site_config' ? 'this site' : source === 'platform_config' ? 'platform default' : 'environment'}):{' '}
            <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">{credentials.app_key_masked}</code>
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <Input
            label="App Key"
            value={appKey}
            onChange={(e) => setAppKey(e.target.value)}
            placeholder={hasSite ? 'Leave empty to keep current' : 'Enter Alibaba App Key'}
            type="password"
          />
          <Input
            label="App Secret"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            placeholder={hasSite ? 'Leave empty to keep current' : 'Enter Alibaba App Secret'}
            type="password"
          />
        </div>

        <p className="text-xs text-stone-400 mb-3">
          Saving here creates a <strong>site-level override</strong> that only applies to this site. Leave empty to inherit the platform default. Platform admins can manage the platform default in <Link to="/admin/platform/settings" className="text-sage-700 underline">Platform Settings</Link>.
        </p>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving || (!appKey && !appSecret)} size="sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Site Override
          </Button>
          {message && <span className={`text-sm ${message.toLowerCase().includes('fail') ? 'text-red-600' : 'text-green-600'}`}>{message}</span>}
        </div>
      </Card>
    </div>
  );
}
